import type {
  AgentTaskRequest,
  AgentTaskResult,
  AgentExecutionRequest,
  AgentExecutionEvent,
  KnowledgeResult,
  KnowledgeProvider,
  RuntimeAdapter,
  AgentProvider,
} from '../core/contracts/index.js';
import type { AgentRegistry } from './agent-registry.js';
import type { AuditLogger } from './audit-log.js';

export class TaskRouter {
  private activeTasks = new Map<string, { request: AgentTaskRequest; status: string }>();
  private completedTasks = new Map<string, AgentTaskResult>();

  constructor(
    private registry: AgentRegistry,
    private auditLogger: AuditLogger,
    private knowledgeProvider?: KnowledgeProvider,
    private runtimeAdapter?: RuntimeAdapter | AgentProvider
  ) {}

  async submitTask(request: AgentTaskRequest): Promise<AsyncIterable<AgentExecutionEvent>> {
    // Idempotency & duplicate check
    if (this.completedTasks.has(request.taskId)) {
      const prev = this.completedTasks.get(request.taskId)!;
      return (async function* () {
        yield {
          executionId: `exec-${request.taskId}`,
          taskId: request.taskId,
          type: 'result' as const,
          result: prev.output,
          timestamp: Date.now(),
        };
      })();
    }

    // Verify agent registration & identity status
    const agentEntry = this.registry.getAgent(request.agentId);
    if (!agentEntry) {
      throw new Error(`Agent ${request.agentId} is not registered.`);
    }

    if (agentEntry.identity.status !== 'active') {
      throw new Error(`Agent ${request.agentId} is ${agentEntry.identity.status} and cannot accept tasks.`);
    }

    // Enforce tenant boundary
    if (agentEntry.identity.tenantId !== request.scope.tenantId) {
      throw new Error(`Tenant mismatch: Agent belongs to ${agentEntry.identity.tenantId}, request for ${request.scope.tenantId}`);
    }

    this.activeTasks.set(request.taskId, { request, status: 'accepted' });
    this.auditLogger.log({
      taskId: request.taskId,
      agentId: request.agentId,
      scope: request.scope,
      status: 'accepted',
      event: `Task "${request.prompt}" accepted by router.`,
    });

    // Resolve context via knowledge provider or direct fallback
    let resolvedContext: KnowledgeResult;
    if (request.knowledgeMode !== 'direct' && this.knowledgeProvider) {
      resolvedContext = await this.knowledgeProvider.query({
        query: request.prompt,
        mode: request.knowledgeMode,
        scope: request.scope,
      });
    } else {
      resolvedContext = {
        modeUsed: 'direct',
        content: request.prompt,
        citations: [],
        queryTimeMs: 0,
      };
    }

    const executionId = `exec-${request.taskId}-${Date.now()}`;
    const executionRequest: AgentExecutionRequest = {
      executionId,
      taskId: request.taskId,
      approvedPrompt: request.prompt,
      resolvedContext,
      scope: request.scope,
      grantedCapabilities: request.requestedCapabilities,
    };

    const self = this;
    const adapter = this.runtimeAdapter;

    return (async function* () {
      self.auditLogger.log({
        taskId: request.taskId,
        agentId: request.agentId,
        scope: request.scope,
        status: 'running',
        event: 'Execution started.',
      });

      if (!adapter) {
        const dummyResult: AgentTaskResult = {
          taskId: request.taskId,
          status: 'completed',
          output: `[Task Router Direct Response]: Processed prompt "${request.prompt}" using mode ${resolvedContext.modeUsed}.`,
          citations: resolvedContext.citations,
          auditLogId: `audit-${request.taskId}`,
          completedAt: Date.now(),
        };
        self.completedTasks.set(request.taskId, dummyResult);
        yield {
          executionId,
          taskId: request.taskId,
          type: 'result' as const,
          result: dummyResult.output,
          timestamp: Date.now(),
        };
        return;
      }

      let finalResultStr = '';
      for await (const event of adapter.execute(executionRequest, request.scope)) {
        if (event.type === 'result' && event.result) {
          finalResultStr = event.result;
          self.completedTasks.set(request.taskId, {
            taskId: request.taskId,
            status: 'completed',
            output: finalResultStr,
            citations: resolvedContext.citations,
            auditLogId: `audit-${request.taskId}`,
            completedAt: Date.now(),
          });
          self.auditLogger.log({
            taskId: request.taskId,
            agentId: request.agentId,
            scope: request.scope,
            status: 'completed',
            event: 'Execution completed successfully.',
          });
        } else if (event.type === 'approval_required') {
          self.auditLogger.log({
            taskId: request.taskId,
            agentId: request.agentId,
            scope: request.scope,
            status: 'awaiting_approval',
            event: `Approval required for capability: ${event.approvalDetails?.capability}`,
          });
        }
        yield event;
      }
    })();
  }

  getTaskResult(taskId: string): AgentTaskResult | undefined {
    return this.completedTasks.get(taskId);
  }
}
